import { takeLatest, delay, takeEvery } from 'redux-saga'
import { call, put, select, all, take, fork, join, race } from 'redux-saga/effects'

import { myLibrarySync, slideExternalSync } from 'sync'
import actionTypes from 'redux/actions/actionTypes'
import callAny from './helpers/call_any'
import googleAuthorizeUser from './helpers/google_authorize_user'
import googleDriveActions from 'redux/actions/googleDriveActions'
import {getGoogleSlideTitle, getGoogleSlideSlides, getSlideThumbnail} from 'helpers/googleDrive'
import { googleSdk } from 'helpers/social'
import gapiSelector from 'redux/selectors/vendors/gapiSelector'
import myLibraryActions from 'redux/actions/my_library_actions'
import formatError from './helpers/error_formatter'
import lessonSources from 'helpers/enums/lessonSources'
import myLibraryLessonSelector from 'redux/selectors/my_library/my_library_lesson_selector'
import googleSlidesMainSelector from 'redux/selectors/google_slides/google_slides_main_selector'
import googleSlidesLessonSelector from 'redux/selectors/google_slides/google_slides_lesson_selector'
import slideTypes from 'helpers/enums/slideTypes'
import getSlidesList from 'views/google_slides/helpers/slides_list'
import errorCodes from 'helpers/enums/errorCodes'
import localErrorCodes from 'helpers/enums/errors/localErrorCodes'
import { NearpodError } from 'redux/sagas/helpers/error_formatter'
import nearpodMetricsHelper from 'sync/helpers/nearpodMetricsHelper'

const EMPTY_TITLE_NAME = 'Missing Title'

function *waitGapiLoaded() {
	let isGapiLoaded = false
	while (!isGapiLoaded) {
		yield call(delay, 500)
		isGapiLoaded = yield select(gapiSelector)
		if (!isGapiLoaded) {
			yield call(delay, 500)
		}
	}
	return true
}


function *getSlideData(presentationId, slides, order) {
	const pageObjectId = slides[order].objectId
	const readonlySlideData = yield call(getSlideThumbnail, presentationId, pageObjectId)

	return {
		slideExternalId: pageObjectId,
		thumbnail: readonlySlideData.contentUrl,
		metadata: JSON.stringify(slides[order]),
		order,
	}
}

function *checkGoogleSlides(action) {
	const trigger = action.trigger
	if (trigger.source === lessonSources.GOOGLE_SLIDES.toString()) {
		switch (trigger.type) {
			case 'save':
				yield call(handleGoogleSlidesSavePresentation, action)
				break
			case 'create':
				yield call(handleGoogleSlidesCreatePresentation, action)
				break

		}
	}
}

function *handleGoogleSlidesCreatePresentation() {
	yield put(myLibraryActions.createGoogleDriveLesson())
}

function *handleGoogleSlidesSavePresentation(action) {
	nearpodMetricsHelper.initialize()
	const trigger = action.trigger
	nearpodMetricsHelper.add({state: 'route_initialize', entityUid: trigger.entityUid})
	let lesson = yield select(myLibraryLessonSelector, { entityUID: trigger.entityUid })

	if (!lesson) {
		yield take(actionTypes.MY_LIBRARY_LESSONS_FETCHED)
		lesson = yield select(myLibraryLessonSelector, { entityUID: trigger.entityUid })
	}
	nearpodMetricsHelper.add({state: 'lesson_ready'})

	yield put(googleDriveActions.startSavePresentationProgressive())
	yield call(saveGoogleSlidesProgressively, trigger.id, lesson)
}

function *handleMyLibraryGoogleSlidesSave(action) {
	nearpodMetricsHelper.initialize()
	nearpodMetricsHelper.add({state: 'my_library_initialize'})
	try {
		if (action.entity.data.sourceInfo.type === lessonSources.GOOGLE_SLIDES) {
			const presentationId = action.entity.data.sourceInfo.payload.id
			nearpodMetricsHelper.add({state: 'my_library_initialize', presentationId})
			yield callAny(myLibrarySync.startGoogleSlidesSavePresentation, presentationId)
			nearpodMetricsHelper.add({state: 'startGoogleSlidesSavePresentation_success'})
			yield call(saveGoogleSlidesProgressively, presentationId, action.entity)
			nearpodMetricsHelper.add({state: 'handleMyLibraryGoogleSlidesSave_ended'})
		}
	} catch (error) {
		const serverError = formatError(error)
		yield put(googleDriveActions.startSavePresentationProgressiveFailed(
			serverError,
			action.entity
		))
		nearpodMetricsHelper.add({state: 'my_library_failed'})
	}
}

function *processSaveSlides(presentationId, currentSlides, entityUID) {
	try {
		return yield callAny(myLibrarySync.processSaveGoogleSlidesSlides, presentationId, currentSlides)
	} catch (error) {
		const serverError = formatError(error)
		nearpodMetricsHelper.add({state: 'processSaveSlides_failed'})
		yield put(googleDriveActions.processSlidesFailed(serverError, presentationId, entityUID))
	}
}


function *saveGoogleSlidesProgressively(presentationId, entity) {
	//we want to cancel save if anything fails (specially the processSaveSlides that's forked so the
	//saveProgressive catch does not catch it)
	yield race([
		call(saveProgressive, presentationId, entity),
		call(cancelSave, entity.data.entityUID, presentationId),
	])
}

function *cancelSave(entityUID, presentationId) {
	while (true) {
		const action = yield take([
			actionTypes.GOOGLE_SLIDES_PROCESS_SLIDES_FAILED,
			actionTypes.GOOGLE_SLIDES_AUTHORIZE_USER_FAILED,
		])
		//checking UID to cancel only the failed process if there's more than one at the same time
		if ((action.type === actionTypes.GOOGLE_SLIDES_PROCESS_SLIDES_FAILED
			&& action.entityUID === entityUID)
			|| (action.type === actionTypes.GOOGLE_SLIDES_AUTHORIZE_USER_FAILED
			&& action.error.error_code !== errorCodes.GOOGLE_SLIDES_POP_UP_BLOCKED)
		) {
			const {error} = action
			nearpodMetricsHelper.add({state: 'savePresentationProgressiveFailed', error})
			yield put(googleDriveActions.savePresentationProgressiveFailed(error, presentationId, entityUID))
			return true
		}
	}
}

function *saveProgressive(presentationId, entity) {
	nearpodMetricsHelper.add({state: 'save_progressive'})
	const entityUID = entity.data.entityUID
	try {
		nearpodMetricsHelper.add({state: 'save_progressive_start'})
		yield call(waitGapiLoaded)
		nearpodMetricsHelper.add({state: 'waitGapiLoaded_success'})

		yield call(googleAuthorizeUser)
		nearpodMetricsHelper.add({state: 'googleAuthorizeUser_success'})

		const presentationData = yield call(getGoogleSlideSlides, presentationId)
		nearpodMetricsHelper.add({state: 'getGoogleSlideSlides_success'})

		const googleSlides = presentationData.slides
		const fileTitle = presentationData.title || EMPTY_TITLE_NAME

		const batch_count = 5
		const totalSlides = googleSlides.length
		let i = 0
		const nearpodSaveSlidesPromises = []
		nearpodMetricsHelper.add({state: 'google_slides_retrieved'})
		yield put(googleDriveActions.startSavePresentationProgressiveUpdate(i, totalSlides, entityUID))
		while (i < totalSlides) {
			const currentSlidesPromises = []
			let count = 0
			while (count < batch_count) {
				if (i < totalSlides) {
					currentSlidesPromises.push(call(getSlideData, presentationId, googleSlides, i))
				}
				i += 1
				count += 1
			}
			const currentSlides = yield all(currentSlidesPromises)

			//after getting the slides from google, we send them to ct those slides and keep getting gs slides
			const task = yield fork(processSaveSlides, presentationId, currentSlides, entityUID)
			nearpodSaveSlidesPromises.push(task)

			yield put(googleDriveActions.startSavePresentationProgressiveUpdate(
				Math.min(i, totalSlides),
				totalSlides,
				entityUID)
			)
			if (i >= totalSlides) {
				break
			} else {
				yield call(delay, 500)
			}
		}
		nearpodMetricsHelper.add({state: 'processSaveSlides_success'})
		//after getting all slides, we wait for all process_slides tasks to finish (saving in ct)
		const saveResponse = yield nearpodSaveSlidesPromises.map(task => join(task))

		if (!saveResponse || !saveResponse.reduce((accum, response) => accum && response, true)) {
			return
			//if the tasks are cancelled or fail, we don't want to continue,
			// doing so will fail when trying to get the payload slideIds and then we would do "put" of
			// the same error twice (probably with different error messages and code)
		}

		const slideIds = saveResponse.reduce((accum, response) => [...accum, ...(response.payload.result)], [])

		//finally publish presentation in ct
		const presentationMetadata = {
			...presentationData,
			slides: null, // Clearing slides data to avoid sending it twice
		}
		nearpodMetricsHelper.add({state: 'endGoogleSlidesSavePresentation_start'})
		const response = yield callAny(myLibrarySync.endGoogleSlidesSavePresentation, presentationId, slideIds, fileTitle, JSON.stringify(presentationMetadata))
		nearpodMetricsHelper.add({state: 'endGoogleSlidesSavePresentation_success'})
		yield put(myLibraryActions.saveChangesFetched(response.payload.result[0], entity))
	} catch (error) {
		const serverError = formatError(error)
		yield put(googleDriveActions.savePresentationProgressiveFailed(
			serverError,
			presentationId,
			entityUID
		))
		nearpodMetricsHelper.add({state: 'save_progressive_failed', error: serverError})
	}
}

function *cancelGoogleSlidesSavePresentation(action) {
	try {
		yield callAny(myLibrarySync.cancelGoogleSlidesSavePresentation, action.presentationExternalId)
	} catch (error) {}
}

function *initializeGoogleSlides(action) {
	try {
		if (!action.params.presentation) {
			throw new NearpodError(
				localErrorCodes.MISSING_PARAMS,
				''
			)
		}

		let fileTitle = action.params.title
		if (!fileTitle) {
			yield call(waitGapiLoaded)
			fileTitle =
				(yield call(getGoogleSlideTitle, action.params.presentation))
				|| EMPTY_TITLE_NAME
		}

		let externalObjectIdsArray = []

		if (!!action.params.slideIds) {
			const slideIds = action.params.slideIds.split(',')

			externalObjectIdsArray = slideIds.map(externalId => ({
				slideExternalId: externalId,
			}))
		}

		const presentationData = yield callAny(myLibrarySync.linkGoogleSlideWithNearpodLesson, action.params.presentation, fileTitle,
			externalObjectIdsArray)

		yield put(googleDriveActions.initializeGoogleSlidesSuccess(presentationData.payload))
	} catch (error) {
		const serverError = formatError(error)
		yield put(googleDriveActions.initializeGoogleSlidesFailed(serverError))
	}
}

function *externalSlideIdObtained(action) {
	try {
		const response = yield callAny(slideExternalSync.saveExternalSlide, action.payload)
		yield put(googleDriveActions.saveGoogleSlidesActivitySuccess(
			response.payload.slide_external.icon,
			response.payload.slide_external.slideExternalId,
			response.payload.slide_external.slideTypeId
		))
	} catch (error) {
		yield put(googleDriveActions.saveGoogleSlidesActivityFailed())
	}
}

function *saveActivity(action) {
	if (action.params.slideExternalId) {
		yield call(externalSlideIdObtained, { payload: action.params })
	}
}

function *getExternalSlideDataForEdit(action) {
	try {
		const googleLessonSelector = yield select(googleSlidesLessonSelector)
		const activityData = yield callAny(
			slideExternalSync.getExternalSlide,
			{
				source: lessonSources.GOOGLE_SLIDES,
				slideExternalId: action.payload.slideExternalId,
				presentationExternalId: googleLessonSelector.data.sourceInfo.payload.id,
			}
		)

		yield put(googleDriveActions.getExternalSlideDataForEditSuccess(activityData))

	} catch (error) {
		const serverError = formatError(error)
		yield put(googleDriveActions.getExternalSlideDataForEditFailed(serverError))
	}
}

function *editLesson(action) {
	try {
		const presentationData = yield callAny(myLibrarySync.editLesson, action.lessonData.entityUID, action.lessonData.folderId, 'true')
		yield put(googleDriveActions.googleSlidesEditLessonSuccess(action.lessonData, presentationData.payload))

	} catch (error) {
		yield put(myLibraryActions.editLessonFailed(error.serverError))
	}
}

function *initializeGoogleApi() {
	try {
		yield call(googleSdk)
	} catch (error) {}
}

function *getSlideType(action) {
	try {
		const googleSelector = yield select(googleSlidesMainSelector)
		const currentSlide = googleSelector.slides.find(slide => slide.slideExternalId === action.slideExternalId)

		if (!currentSlide) {
			const slideList = [
				{
					slideExternalId: action.slideExternalId,
				},
			]

			const response = yield callAny(
				myLibrarySync.getGoogleSlideTypes,
				{
					source: lessonSources.GOOGLE_SLIDES,
					slides: JSON.stringify(slideList),
				}
			)

			const currentSlideData = {
				slideExternalId: action.slideExternalId,
				isNearpodSlide: response.payload.slide_external_list[0].isNearpodSlide,
				slideTypeId: response.payload.slide_external_list[0].slideTypeId,
			}

			yield put(googleDriveActions.googleSlidesGetSlideTypeSuccess(currentSlideData))

		} else {
			const currentSlideIndex = googleSelector.slides.indexOf(currentSlide)
			yield put(googleDriveActions.googleSlidesChangeCurrentSlideIndex(currentSlideIndex))
		}
	} catch (error) {}
}

function *convertToDrawIt() {
	try {
		const googleLessonSelector = yield select(googleSlidesLessonSelector)
		const googleMainSelector = yield select (googleSlidesMainSelector)
		const lessonExternalId = googleLessonSelector.data.sourceInfo.payload.id
		const currentSlideId = googleMainSelector.slides[googleMainSelector.currentSlideIndex].slideExternalId
		const thumbnail = yield call(getSlideThumbnail, lessonExternalId, currentSlideId)
		const slideData = yield callAny(myLibrarySync.convertToDrawIt, lessonExternalId, thumbnail.contentUrl)
		const slidesList = getSlidesList()
		const activityData = {
			slideId: slideData.payload.slide,
			presentationExternalId: lessonExternalId,
			overlay: slidesList.find(slide => slide.id === slideTypes.SLIDE_TYPE_ID_DRAW_IT).overlay,
		}
		yield put(googleDriveActions.googleSlidesConvertToDrawItGetThumbnailSuccess(activityData))
	} catch (error) {
		yield put(googleDriveActions.googleSlidesConvertToDrawItFailed())
	}
}

export default [
	takeLatest(actionTypes.GOOGLE_SLIDES_INITIALIZE, initializeGoogleSlides),
	takeLatest(actionTypes.MY_LIBRARY_INITIALIZE, checkGoogleSlides),
	takeLatest([actionTypes.GOOGLE_SLIDES_SLIDE_EXTERNAL_ID_OBTAINED], externalSlideIdObtained),
	takeLatest(actionTypes.GOOGLE_SLIDES_INITIALIZE_ACTIVITY_SAVE, saveActivity),
	takeLatest(actionTypes.GOOGLE_SLIDES_EDIT_LESSON, editLesson),
	takeEvery(actionTypes.MY_LIBRARY_SAVE_CHANGES, handleMyLibraryGoogleSlidesSave),
	takeLatest(actionTypes.GOOGLE_SLIDES_ACTIVITY_EDIT_EXTERNAL_ID_OBTAINED, getExternalSlideDataForEdit),
	takeLatest(actionTypes.MAIN_TOPBAR_CONNECTOR_INITIALIZE, initializeGoogleApi),
	takeEvery(actionTypes.GOOGLE_SLIDES_SAVE_PRESENTATION_PROGRESSIVE_FAILED, cancelGoogleSlidesSavePresentation),
	takeLatest(actionTypes.GOOGLE_SLIDES_CURRENT_SLIDE_CHANGED, getSlideType),
	takeLatest(actionTypes.GOOGLE_SLIDES_CONVERT_TO_DRAW_IT, convertToDrawIt),
]

export {
	initializeGoogleSlides,
	getSlideData,
	waitGapiLoaded,
	externalSlideIdObtained,
	editLesson,
	handleMyLibraryGoogleSlidesSave,
	getExternalSlideDataForEdit,
	saveActivity,
	checkGoogleSlides,
	saveProgressive,
	processSaveSlides,
	cancelSave,
	getSlideType,
	convertToDrawIt,
	saveGoogleSlidesProgressively,
}
